/**
 * SellerRatingBadge — kompakter Ø-Sterne-Badge für Artikelkarten & Header.
 * Zeigt AUSSCHLIESSLICH Reviews aus dem gerade offenen Bereich (STX/MP/Social).
 *
 * Owner-Regel (iter88.21): Sortenrein → wenn Karte im STX-Bereich gerendert wird,
 * werden nur STX-Bewertungen des Sellers geladen (keine Cross-Area-Mittelung).
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Star } from "lucide-react";
import api from "@/lib/api";

const AREA_MAP = { STX: "STILLTOUCHEDX", MP: "MARKETPLACE", SOCIAL: "SOCIAL" };

export default function SellerRatingBadge({ sellerId, area = "MP", size = "sm", showCount = true, className = "" }) {
  const { t } = useTranslation();
  const [avg, setAvg] = useState(null);
  const [count, setCount] = useState(0);
  const scopeArea = AREA_MAP[area] || area;

  useEffect(() => {
    if (!sellerId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/reviews/seller-summary/${sellerId}?area=${encodeURIComponent(scopeArea)}`);
        if (cancelled) return;
        setAvg(data?.avg_rating ?? 0);
        setCount(data?.review_count ?? 0);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [sellerId, scopeArea]);

  if (avg === null) return null;
  if (count === 0) return null;

  const iconSize = size === "lg" ? 15 : size === "md" ? 13 : 11;
  const textSize = size === "lg" ? "text-sm" : "text-xs";

  return (
    <div data-testid={`seller-rating-${sellerId}`}
      className={`inline-flex items-center gap-1 ${textSize} text-[#666] [.stx-theme:not(.stx-light)_&]:text-stx-text-muted ${className}`}
      title={t("reviews.badge_title", { avg, count, area: scopeArea })}>
      <Star size={iconSize} className="fill-[#f5b400] text-[#f5b400]" />
      <span className="font-medium text-[#0A0A0A] [.stx-theme:not(.stx-light)_&]:text-stx-text">{avg.toFixed(1)}</span>
      {showCount && <span className="text-[#999] [.stx-theme:not(.stx-light)_&]:text-stx-text-dim">({count})</span>}
    </div>
  );
}
